/**
 * Newsletter form — subscribes an email address to a Klaviyo list.
 *
 * Markup is snippets/newsletter-form.liquid. Credentials come from theme
 * settings and arrive here as data attributes, so this file knows nothing about
 * where on the page it is: the same component serves the footer, and will serve
 * a popup or a blog form without a change.
 *
 * THE ENDPOINT
 *
 *   POST https://a.klaviyo.com/client/subscriptions?company_id=<public key>
 *   Content-Type: application/vnd.api+json
 *   revision: 2026-07-15
 *
 * Klaviyo's "Create Client Subscription". It takes the PUBLIC api key (site ID)
 * only — never a private key — and is explicitly designed to be called from a
 * browser, so the key being visible in the page source is by design, not a leak.
 *
 * Success is 202 Accepted with an EMPTY BODY. Do not try to parse it.
 *
 * FOUR THINGS ABOUT THIS ENDPOINT WERE VERIFIED AGAINST THE LIVE API RATHER THAN
 * ASSUMED, because each would have failed silently:
 *
 *   1. The path has NO trailing slash. Both forms answer, but the trailing-slash
 *      variant is routed through Klaviyo's bot protection and sets a cookie; the
 *      bare path is the one in their OpenAPI spec and the one that stays clean.
 *   2. There is no redirect on POST. A 301 would have been fatal — browsers
 *      downgrade a redirected POST to GET and drop the body.
 *   3. The CORS preflight advertises `Access-Control-Allow-Methods: OPTIONS`
 *      and nothing else. That LOOKS like it blocks us and does not: POST is a
 *      CORS-safelisted method, which the Fetch spec exempts from the
 *      allow-methods check. Do not "fix" this by switching content type to dodge
 *      the preflight — the `revision` header triggers it regardless.
 *   4. `Access-Control-Allow-Headers` covers `content-type, revision`, and the
 *      response pairs `Allow-Origin: *` with `Allow-Credentials: true`. That
 *      combination is rejected by browsers if credentials are sent, which is why
 *      `credentials: 'omit'` below is load-bearing rather than tidy.
 *
 * DOUBLE OPT-IN CHANGES WHAT SUCCESS MEANS. If the Klaviyo list has double
 * opt-in enabled, a 202 means "confirmation email sent", not "subscribed". The
 * success message is a merchant setting precisely so it can say the right thing
 * for how their list is configured.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: same reason as
 * every other component here — section JS is bundled and injected by Shopify in
 * an order we cannot see, and it was measured NOT running at all on this store.
 */
if (!customElements.get('newsletter-form')) {
  customElements.define(
    'newsletter-form',
    class NewsletterForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-newsletter-form]');
        this.input = this.querySelector('[data-newsletter-input]');
        this.submit = this.querySelector('[data-newsletter-submit]');
        this.status = this.querySelector('[data-newsletter-status]');

        if (!this.form || !this.input || !this.submit || !this.status) return;

        /**
         * Validation moves from the browser to us, so that a bad address is
         * announced in the same live region as every other outcome instead of a
         * native bubble that vanishes on its own.
         *
         * Set HERE rather than in the markup deliberately: if this script fails
         * to load, the attribute is absent and the browser still refuses to
         * submit an invalid address. A worse fallback than ours, but a fallback.
         */
        this.form.noValidate = true;

        this.pending = false;
        this.handleSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.handleSubmit);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.handleSubmit);

        /**
         * Abort any request still in flight. The theme editor re-renders a
         * section on every setting change, so this element is removed and
         * rebuilt constantly — without this, a response could land on a detached
         * element and write to a status region nobody can see.
         */
        if (this.controller) this.controller.abort();
      }

      async handleSubmit(event) {
        event.preventDefault();

        /** A second Enter press while the first request is open. */
        if (this.pending) return;

        const email = this.input.value.trim();

        if (!email || !this.input.checkValidity()) {
          this.setStatus(this.dataset.messageInvalid);
          this.input.focus();
          return;
        }

        const companyId = this.dataset.companyId;
        const listId = this.dataset.listId;

        /**
         * Unconfigured. The visitor gets the ordinary failure message — telling
         * them the shop has not pasted in an API key helps nobody — while the
         * console says exactly what is wrong for whoever is debugging it.
         */
        if (!companyId || !listId) {
          this.setStatus(this.dataset.messageError);
          console.warn(
            '[newsletter-form] Missing Klaviyo public API key or list ID. Both are set in Theme settings → Klaviyo.',
          );
          return;
        }

        this.setPending(true);

        try {
          this.controller = new AbortController();

          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
            {
              method: 'POST',
              /** See note 4 in the header — this is required, not defensive. */
              credentials: 'omit',
              signal: this.controller.signal,
              headers: {
                'Content-Type': 'application/vnd.api+json',
                revision: '2026-07-15',
              },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: this.dataset.source,
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: {
                          email: email,
                          subscriptions: {
                            email: { marketing: { consent: 'SUBSCRIBED' } },
                          },
                        },
                      },
                    },
                  },
                  relationships: {
                    list: { data: { type: 'list', id: listId } },
                  },
                },
              }),
            },
          );

          if (response.status === 202) {
            this.setStatus(this.dataset.messageSuccess);
            this.form.reset();
          } else {
            this.setStatus(this.dataset.messageError);
            console.warn(
              `[newsletter-form] Klaviyo returned ${response.status}.`,
            );
          }
        } catch (error) {
          /**
           * An abort is us tearing the component down, not a failure — writing
           * an error into a region that is about to be discarded would be wrong,
           * and on a theme-editor re-render it would flash a false error.
           */
          if (error.name === 'AbortError') return;

          this.setStatus(this.dataset.messageError);
          console.warn('[newsletter-form] Request failed.', error);
        } finally {
          this.setPending(false);
        }
      }

      /**
       * `aria-busy` on the form tells a screen reader the region is mid-update.
       * The button is disabled rather than hidden so the layout does not move
       * and focus is not thrown to the top of the document.
       */
      setPending(pending) {
        this.pending = pending;
        this.submit.disabled = pending;
        this.form.setAttribute('aria-busy', pending ? 'true' : 'false');
      }

      /**
       * The one way anything reaches the visitor. Every outcome is TEXT in a
       * live region — never a colour change, which WCAG 1.4.1 does not accept as
       * the sole indicator, and never an alert() the page cannot style.
       */
      setStatus(message) {
        this.status.textContent = message || '';
      }
    },
  );
}
